import { useState } from "react";
import { CheckCircle2, Circle, ClipboardCheck, ListChecks, PenLine, Award, FileText, Download } from "lucide-react";
import { toast } from "sonner";
import { download, errMsg, openFile } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useApi, Loading, ErrorNote, StatusChip, btn, inputCls, Empty } from "./kit";
import { SectionView } from "../pages/CoursePlayer";
import { WalkthroughStep } from "./Walkthrough";

function LocalQuiz({ quiz, passMark, onPassed }) {
  const [answers, setAnswers] = useState({});
  const [attempts, setAttempts] = useState([]);
  const last = attempts[0];
  const submit = () => {
    const right = quiz.questions.filter((q) => answers[q.id] === q.correct).length;
    const score = Math.round((right * 100) / quiz.questions.length);
    const passed = score >= passMark;
    setAttempts([{ score, passed, answers: { ...answers } }, ...attempts]);
    toast[passed ? "success" : "error"](passed ? `Passed with ${score}%` : `${score}% — pass mark is ${passMark}%.`);
    if (passed) onPassed();
  };
  return (
    <div className="space-y-6">
      <div><h2 className="text-2xl font-semibold">Scenario quiz</h2><p className="text-muted-foreground">Pass mark {passMark}%. In preview, attempts are not saved.</p></div>
      {quiz.questions.map((q, i) => {
        const fb = last && last.answers[q.id] !== undefined ? last.answers[q.id] === q.correct : null;
        return (
          <fieldset key={q.id} className="rounded-2xl border bg-white p-5" data-testid={`preview-quiz-question-${i}`}>
            <legend className="sr-only">Question {i + 1}</legend>
            <p className="font-semibold mb-3">{i + 1}. {q.prompt}</p>
            <div className="grid gap-2">{q.options.map((o, j) => (
              <label key={j} className={`tap flex items-center gap-3 rounded-lg border px-4 cursor-pointer ${answers[q.id] === j ? "border-[color:var(--ink)] bg-muted" : ""}`}>
                <input type="radio" name={`pv-${q.id}`} checked={answers[q.id] === j} onChange={() => setAnswers({ ...answers, [q.id]: j })} data-testid={`preview-q${i}-option-${j}`} className="h-5 w-5 accent-[color:var(--dcbc-red)]" /> {o}
              </label>))}</div>
            {fb !== null && <p className={`mt-3 rounded-lg p-3 text-sm ${fb ? "bg-emerald-50 text-emerald-900" : "bg-rose-50 text-rose-900"}`}><b>{fb ? "Correct." : "Not quite."}</b> {q.explanation}</p>}
          </fieldset>
        );
      })}
      <button className={btn.primary} disabled={Object.keys(answers).length < quiz.questions.length} onClick={submit} data-testid="preview-quiz-submit">{attempts.length ? "Submit another attempt" : "Submit answers"}</button>
    </div>
  );
}

function LocalAck({ p, user, done, onDone }) {
  const [opened, setOpened] = useState(false);
  const [checked, setChecked] = useState(false);
  const [name, setName] = useState("");
  const ver = p.current || (p.versions || [])[0];
  const open = () => openFile(ver.file_id).then(() => setOpened(true)).catch((e) => toast.error(errMsg(e)));
  const ok = name.trim().toLowerCase() === user.name.trim().toLowerCase();
  return (
    <div className="rounded-2xl border bg-white p-5" data-testid={`preview-ack-${p.id}`}>
      <div className="mb-2 flex items-center justify-between gap-3"><h3 className="text-lg font-semibold">{p.title}</h3>
        {done ? <StatusChip status="completed" label="Acknowledged (preview)" /> : <StatusChip status="not_started" label="Not acknowledged" />}</div>
      {!ver ? <Empty>No document uploaded yet — staff would be blocked here.</Empty> : done ? <p className="text-sm text-muted-foreground">Acknowledged as “{name}”.</p> : (
        <div className="space-y-4">
          {!p.current && <p className="text-sm text-amber-800">Showing the unpublished draft version {ver.version}. Staff will see the published version.</p>}
          <button onClick={open} className={btn.ghost} data-testid={`preview-ack-open-${p.id}`}><FileText className="h-4 w-4" /> Open version {ver.version} {opened && "(opened)"}</button>
          <label className="flex items-start gap-3 text-[15px]"><input type="checkbox" checked={checked} disabled={!opened} onChange={(e) => setChecked(e.target.checked)} data-testid={`preview-ack-check-${p.id}`} className="mt-1 h-5 w-5 accent-[color:var(--dcbc-red)]" />
            <span>I confirm that I opened and read the {p.title} (version {ver.version}, effective {ver.effective_date || "date not set"}), that I understand it, and that I agree to follow it.</span></label>
          <div><label className="text-sm font-semibold">Type your full name: <span className="font-normal text-muted-foreground">{user.name}</span></label>
            <input className={inputCls} data-testid={`preview-ack-name-${p.id}`} value={name} disabled={!opened} onChange={(e) => setName(e.target.value)} /></div>
          <button className={btn.primary} disabled={!opened || !checked || !ok} onClick={onDone} data-testid={`preview-ack-submit-${p.id}`}><PenLine className="h-4 w-4" /> Acknowledge</button>
        </div>)}
    </div>
  );
}

export default function PreviewPlayer({ cid, which = "draft" }) {
  const { user } = useAuth();
  const { data, loading, error } = useApi(`/courses/${cid}`);
  const policies = useApi("/policies");
  const settings = useApi("/settings");
  const [step, setStep] = useState(0);
  const [done, setDone] = useState({});
  if (loading || policies.loading || settings.loading) return <Loading />;
  if (error) return <ErrorNote error={error} />;
  const v = (which === "published" && data.published) || data.draft || data.published;
  const pols = (policies.data || []).filter((p) => v.policy_ids.includes(p.id));
  const passMark = v.quiz?.pass_mark || settings.data?.pass_mark || 80;
  const steps = [
    ...v.sections.map((s) => ({ key: s.id, kind: "sec", title: s.title, sec: s })),
    ...(v.quiz?.questions?.length ? [{ key: "quiz", kind: "quiz", title: "Scenario quiz" }] : []),
    ...(pols.length ? [{ key: "ack", kind: "ack", title: "Policy acknowledgements" }] : []),
    ...(v.signoff?.required ? [{ key: "wt", kind: "wt", title: "Manager sign-off" }] : []),
    { key: "done", kind: "done", title: "Completion record" },
  ];
  const isDone = (s) => s.kind === "ack" ? pols.every((p) => done[`ack-${p.id}`]) : s.kind === "done" ? steps.slice(0, -1).every(isDone) : !!done[s.key];
  const cur = steps[Math.min(step, steps.length - 1)];
  const mark = (k) => setDone((d) => ({ ...d, [k]: true }));
  const icons = { sec: Circle, quiz: ListChecks, ack: PenLine, wt: ClipboardCheck, done: Award };
  const go = (i) => { setStep(i); window.scrollTo({ top: 0 }); };
  const pdf = (kind) => download(`/courses/${cid}/preview-pdf?kind=${kind}&version=${v.status === "published" ? "published" : "draft"}`, `PREVIEW_${kind}.pdf`).catch((e) => toast.error(errMsg(e)));
  return (
    <div data-testid="preview-player">
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <h1 className="text-3xl font-semibold" data-testid="preview-title">{v.title}</h1>
        <span className="text-sm text-muted-foreground">Version {v.version}</span>
        <StatusChip status={steps.slice(0, -1).every(isDone) ? "completed" : Object.keys(done).length ? "in_progress" : "not_started"} />
      </div>
      <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
        <ol className="flex gap-2 overflow-x-auto lg:flex-col lg:overflow-visible pb-2" aria-label="Course steps">
          {steps.map((s, i) => { const Icon = isDone(s) ? CheckCircle2 : icons[s.kind]; return (
            <li key={s.key} className="shrink-0"><button onClick={() => go(i)} data-testid={`preview-step-${i}`} aria-current={i === step}
              className={`tap flex w-full items-center gap-3 rounded-lg border px-3 text-left text-sm font-medium ${i === step ? "bg-[color:var(--ink)] text-white border-transparent" : "bg-white"}`}>
              <Icon className={`h-4 w-4 shrink-0 ${isDone(s) && i !== step ? "text-emerald-600" : ""}`} aria-hidden="true" /><span className="lg:whitespace-normal whitespace-nowrap">{s.title}</span></button></li>); })}
        </ol>
        <div className="min-w-0 fade-up" key={step}>
          {cur.kind === "sec" && <SectionView sec={cur.sec} n={step + 1} canAct done={!!done[cur.key]} onDone={() => { mark(cur.key); go(Math.min(step + 1, steps.length - 1)); }} />}
          {cur.kind === "quiz" && <LocalQuiz quiz={v.quiz} passMark={passMark} onPassed={() => mark("quiz")} />}
          {cur.kind === "ack" && (
            <div className="space-y-4"><h2 className="text-2xl font-semibold">Policy acknowledgements</h2>
              <p className="text-muted-foreground">Open each document, tick the box and type your name. Managers can never acknowledge for you.</p>
              {pols.map((p) => <LocalAck key={p.id} p={p} user={user} done={!!done[`ack-${p.id}`]} onDone={() => mark(`ack-${p.id}`)} />)}</div>)}
          {cur.kind === "wt" && <div className="space-y-3"><WalkthroughStep a={{}} v={v} /><button className={btn.ghost} onClick={() => { mark("wt"); go(step + 1); }} data-testid="preview-walkthrough-skip">Pretend your manager signed off</button></div>}
          {cur.kind === "done" && (
            <div className="rounded-2xl border bg-white p-8 text-center" data-testid="preview-completion">
              <Award className="mx-auto h-12 w-12 text-[color:var(--dcbc-red)]" />
              <h2 className="mt-3 text-2xl font-semibold">{isDone(cur) ? "Course complete" : "Not complete yet"}</h2>
              <p className="mt-1 text-sm text-muted-foreground">These are sample documents with your name, so you can check the design.</p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <button className={btn.primary} onClick={() => pdf("completion")} data-testid="preview-completion-pdf"><Download className="h-4 w-4" /> Completion PDF</button>
                {v.welcome_certificate && <button className={btn.dark} onClick={() => pdf("certificate")} data-testid="preview-certificate-pdf"><Award className="h-4 w-4" /> Welcome certificate</button>}
              </div>
            </div>)}
        </div>
      </div>
    </div>
  );
}
